/** A report alert's link (`#/developer/reports[/<id>]`, core/router.ts
 *  `reportLink`) reaches Developer > Reports even when that tab cannot show
 *  the moment the link lands. At a cold start from a tapped alert the account
 *  is still loading, and the tab is there only for a developer account with
 *  Developer Mode on, so main.ts sends a visit to a hidden tab Home. The link
 *  waits for the account, opens the tab as soon as it can (the Developer tab
 *  then opens the report itself), and otherwise says why, where the reader is
 *  looking: Developer Mode off (Open Settings; the report opens once it is
 *  turned on), not a developer account, or signed out. Nothing is decided
 *  about the account before it has loaded or a few seconds have passed. */
import { activeTab, navigate, reportLink } from '../core/router';
import { currentProfile } from '../services/account/accountService';
import { devMode, isCurrentAdmin } from '../services/account/adminService';
import { toast } from './widgets';

/** How long a link waits for the account to load before saying why it
 *  cannot open. A developer with Developer Mode off is told at once. */
const SETTLE_MS = 8000;

let stop: (() => void) | null = null;

const canOpen = (): boolean => devMode.get() && isCurrentAdmin();

function explain(): void {
  const abuse = reportLink.get()?.section === 'abuse';
  if (!currentProfile.get()) {
    toast(abuse ? 'Log in with a TDG developer account to open that incident.' : 'Log in with a TDG developer account to open that report.', 'info', 9000,
      { label: 'Open Community', run: () => navigate('community') });
    return;
  }
  if (!isCurrentAdmin()) {
    toast(abuse ? 'Abuse Watch opens only for TDG developer accounts, and this account is not one.'
      : 'Reports open only for TDG developer accounts, and this account is not one.', 'info', 9000);
    return;
  }
  // Already on the Developer tab: its restricted face says this, with Open Settings.
  if (activeTab.get() === 'developer') return;
  toast(abuse ? 'An abuse incident is waiting. Turn on Developer Mode in Settings and it opens.'
    : 'A report is waiting. Turn on Developer Mode in Settings and it opens.', 'info', 12000,
    { label: 'Open Settings', run: () => navigate('settings') });
}

export function installReportLinkDoor(): void {
  reportLink.subscribe((link) => {
    stop?.();
    stop = null;
    if (!link || canOpen()) return; // the Developer tab is there and answers it
    const seq = link.seq;
    let told = false;
    const tell = (): void => { if (!told) { told = true; explain(); } };
    const settle = (): void => {
      if (reportLink.get()?.seq !== seq) return;
      if (canOpen()) { stop?.(); stop = null; navigate('developer'); return; }
      // A developer account with Developer Mode off: no need to wait. Told
      // after the router has finished (it names the link, then the tab), so
      // a reader already shown the Developer tab's restricted face is not
      // told twice.
      if (isCurrentAdmin()) queueMicrotask(tell);
    };
    const timer = window.setTimeout(tell, SETTLE_MS);
    const offProfile = currentProfile.subscribe(settle);
    const offMode = devMode.subscribe(settle);
    stop = () => { window.clearTimeout(timer); offProfile(); offMode(); };
    settle();
  }, true);
}
